import { formatOrderCalendarDay } from '@engyalo/delivery-ui-components'
import type {
  DialogProps,
  MessageDetailRow,
} from '@engyalo/delivery-ui-components'
import {
  CheckoutRules,
  type IConfirmOrderDialogConfig,
} from '../interfaces'
import { TextLibrary } from '../consts/defaultConfigValues/defaultConstants'
import type { RootState } from '../redux/store'
import formatPrice from './formatPrice'
import { isHidePricesEnabled } from './hidePrices'
import {
  selectRulePick,
  selectRulePickLabel,
} from './checkoutRuleSelection'

/**
 * The dialog props this adapter owns, checked against the library's own prop
 * shape.
 *
 * Same guard as `InjectedCartCheckoutProps` in `cartCheckoutPanel.ts`, for the
 * same reason: what this returns is spread over a hardcoded object and then
 * over `DialogProps`, so a library rename would compile, land unread, and drop
 * the block in silence.
 *
 * **It guards names and value types, not presence.** Every prop here is
 * optional, so `{}` satisfies it — which is deliberate, since `{}` is what a
 * storefront that has not opted in gets. Dropping a line from the returned
 * object is a compile success, so each one has a test naming it.
 */
type InjectedConfirmOrderProps = Pick<
  DialogProps,
  'details' | 'hidePrices' | 'currency' | 'currencyCode' | 'message'
>

/**
 * One row, or none.
 *
 * The library drops a row whose `label` or `value` is missing, blank or not a
 * string, so an absent value would be harmless to pass. Withholding it here
 * anyway keeps the *authored index* stable: the library numbers each row's
 * `data-testid` by its position in this array, and a test that asserts a
 * suppressed row is absent reads better against a fixed numbering than against
 * one that shifts when a sibling drops out.
 */
const row = (
  label: TextLibrary,
  value: string | undefined,
  extra: Pick<MessageDetailRow, 'emphasis' | 'containsPrice'> = {},
): MessageDetailRow[] => (value ? [{ label, value, ...extra }] : [])

/**
 * The order's total, preferring the summary's copy.
 *
 * The dialog fires from the cart footer as well as the summary's, and
 * `useGetSummary` runs only on the summary template — so on a storefront whose
 * cart places the order directly, `summarySlice` still holds its initial `0`.
 * `items` rather than `total` is the test for that, because a `0` total is a
 * legitimate value and an empty item list is not something a loaded summary
 * produces.
 */
const readTotal = (state: RootState): number =>
  state.summarySlice.items.length > 0
    ? state.summarySlice.total
    : state.cartSlice.total

/**
 * The tenant's word for a backend payment enum value, or the value itself.
 *
 * `override` wins when it is a non-empty string. That is the picked option's
 * `attributes.label` (guide §5) — the storefront offers keyed options sharing
 * one `paymentMethod` and the shopper's "Crédito 14 días" has to survive the
 * `CREDIT` collapse. Mirrors the library's `resolvePaymentLabel` on
 * `CartCheckoutPanel.utils.ts` (COM-1252).
 *
 * `??` rather than `||` on the map lookup, so a tenant mapping a method to
 * `''` still withdraws that row deliberately when no override is present.
 * The override bends that suppression — the same semantics the library panel
 * ships: a per-pick label beats a map authored for the pre-keyed world.
 *
 * An unmapped method renders raw, which is visible and diagnosable where a
 * missing row is not.
 */
const resolvePaymentLabel = (
  method: string,
  override: string | undefined,
  labels: IConfirmOrderDialogConfig['paymentMethodLabels'],
): string => {
  if (typeof override === 'string' && override) return override
  return labels?.[method] ?? method
}

/**
 * The `details` block (plus money gate and currency marks) and the optional
 * warning-emphasis `message` override, for the `confirmOrder` dialog.
 *
 * The two halves are forwarded independently. Returns `{}` only when the
 * storefront has not authored `config.dialogs.confirmOrder` at all. With the
 * block authored:
 *
 * - **`message` (COM-1279)** rides on `authored.message`. A storefront that
 *   authors only `sx` keeps the standard `CART_CONFIRM_MESSAGE` body — the
 *   `value` defaults to that token before `authored.message` spreads, so an
 *   author who provides `value` still wins. An absent `authored.message`
 *   forwards no `message` at all, so the static half from
 *   `getValidatorDialogsProps` stands.
 * - **`details`** gates on at least one pick for the two M2 rules (delivery
 *   dates and payment methods). A read-only rule carries a backend-set value
 *   and therefore a pick; an editable one cannot reach *Realizar pedido*
 *   unpicked, because COM-1046 holds the CTA at *Continuar* until the shopper
 *   chooses. A storefront serving neither rule has neither pick, and a lone
 *   Total row repeats nothing — the function returns the message override
 *   alone in that case.
 *
 * **The authored block is still the opt-in for both halves.** `defaultConfig.ts`
 * authors no `dialogs` key, so today every storefront takes the `{}` branch
 * and keeps the confirm dialog it has had all along.
 *
 * ⚠️ **`formatPrice` is called without `hidePrices` on purpose.** Passing the
 * flag makes it return the `__HIDDEN_PRICE__` sentinel, which is not a string
 * to put in front of a shopper and which the library would render verbatim.
 * Suppression is the library's job: the Total row declares `containsPrice`,
 * `hidePrices` rides alongside it, and `buildDetailRows` drops the row while
 * the dialog still opens on its other two. `maskPricesForDisplay` cannot stand
 * in either — it zeroes *numbers* under money-named keys, and every value here
 * is a formatted string.
 *
 * Labels are emitted as unresolved `TextLibrary` tokens, the way
 * `buildRowAction` emits `DELIVERY_DATE_CHANGE_LABEL`. `components/Dialog.tsx`
 * runs the whole dialog object through `getCinnamonProps`, and `processData`
 * recurses into arrays of objects, so a token inside `details.rows[].label`
 * resolves there. One that does not resolve becomes `undefined` and costs its
 * own row and nothing else.
 */
export const getConfirmOrderDialogProps = (
  state: RootState,
): InjectedConfirmOrderProps => {
  const authored = state.defaultSlice.config?.dialogs?.confirmOrder
  if (!authored) return {}

  /*
   * Warning-emphasis on the dialog's message (COM-1279). Forwarded
   * independently of the money gate below: a storefront that opts into the
   * authored block for `sx` alone has no reason to also carry delivery or
   * payment picks. `value` defaults to the standard token so authoring only
   * `sx` keeps the body intact; an authored `value` wins because it spreads
   * second.
   */
  const messageOverride: Pick<InjectedConfirmOrderProps, 'message'> =
    authored.message
      ? {
          message: {
            value: TextLibrary.CART_CONFIRM_MESSAGE,
            ...authored.message,
          },
        }
      : {}

  const deliveryPick = selectRulePick(state, CheckoutRules.DELIVERY_DATES)
  const paymentPick = selectRulePick(state, CheckoutRules.PAYMENT_METHODS)
  if (!deliveryPick && !paymentPick) return messageOverride

  const options = state.defaultSlice.config?.options
  const currency = options?.currency || options?.currencySymbol
  const currencyCode = options?.currencyPrefix || options?.currencyCode

  /*
   * COM-1240. The storefront's `currencyDisplay` is forwarded only when it
   * carries at least one of `{{symbol}}` or `{{code}}`. A template that
   * drops both — `mondelez-mx-b2b-ng`'s `"{{price}}"` is the live case —
   * leaves the Total a bare number the shopper cannot tell MXN from USD
   * from, so we hand `undefined` and `formatPrice`'s default
   * `"{{symbol}}{{price}}{{code}}"` kicks in. A reorder like
   * `"{{code}}{{price}}{{symbol}}"` keeps one of the marks and is honored.
   */
  const authoredDisplay = options?.currencyDisplay
  const safeCurrencyDisplay =
    authoredDisplay &&
    (authoredDisplay.includes('{{symbol}}') ||
      authoredDisplay.includes('{{code}}'))
      ? authoredDisplay
      : undefined

  const { backgroundColor, labelColor, valueColor } = authored

  return {
    ...messageOverride,
    details: {
      rows: [
        ...row(
          TextLibrary.CONFIRM_ORDER_TOTAL_LABEL,
          formatPrice({
            price: readTotal(state),
            currency,
            code: currencyCode,
            format: options?.format,
            display: safeCurrencyDisplay,
          }),
          { containsPrice: true, emphasis: true },
        ),
        ...row(
          TextLibrary.CONFIRM_ORDER_DELIVERY_LABEL,
          deliveryPick &&
            formatOrderCalendarDay(deliveryPick, options?.localization),
        ),
        ...row(
          TextLibrary.CONFIRM_ORDER_PAYMENT_LABEL,
          paymentPick &&
            resolvePaymentLabel(
              paymentPick,
              selectRulePickLabel(state, CheckoutRules.PAYMENT_METHODS),
              authored.paymentMethodLabels,
            ),
        ),
      ],
      /*
       * Absent rather than `undefined` when unauthored, so the library falls
       * back to its own theme defaults instead of being handed a prop cinnamon
       * has decided not to own. Same reading as `getCartCheckoutPanelProps`'
       * two action props.
       */
      ...(backgroundColor ? { backgroundColor } : {}),
      ...(labelColor ? { labelColor } : {}),
      ...(valueColor ? { valueColor } : {}),
    },
    hidePrices: isHidePricesEnabled(state),
    ...(currency ? { currency } : {}),
    ...(currencyCode ? { currencyCode } : {}),
  }
}

export default getConfirmOrderDialogProps
